import CallIcon from '@mui/icons-material/Call'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import ReplyIcon from '@mui/icons-material/Reply'
import { Box, Link, Typography } from '@mui/material'
import PropTypes from 'prop-types'
import React from 'react'
import { previewButtons } from '../helpers/whatsappTemplate'
import { ICON, RADIUS, WHATSAPP_PREVIEW } from '../theme/tokens'

const BUTTON_ICON = {
    url: OpenInNewIcon,
    phone: CallIcon,
    quick_reply: ReplyIcon,
    copy_code: ContentCopyIcon,
}

const MEDIA_LABEL = { IMAGE: 'Image', VIDEO: 'Video', DOCUMENT: 'Document' }

const tileSx = {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    background: WHATSAPP_PREVIEW.tile,
    borderRadius: `${RADIUS.sm}px`,
    px: 1.25,
    py: 1,
    mb: 0.75,
}

/**
 * A header's media in the bubble: the image, a video player, or a document
 * tile with the file name (and an Open link when there is a link to it).
 * With no link (a saved template whose file could not be signed), a tile
 * names the kind of media instead of a broken image.
 */
const HeaderMedia = ({ media }) => {
    const label = MEDIA_LABEL[media.kind] ?? 'Media'
    const mediaSx = {
        display: 'block',
        width: '100%',
        maxHeight: 240,
        objectFit: 'cover',
        borderRadius: `${RADIUS.sm}px`,
        mb: 0.75,
    }
    if (media.url && media.kind === 'IMAGE')
        return (
            <Box
                component="img"
                data-testid="wa-preview-media"
                src={media.url}
                alt={`Header image${
                    media.fileName ? ` ${media.fileName}` : ''
                }`}
                sx={mediaSx}
            />
        )
    if (media.url && media.kind === 'VIDEO')
        return (
            <Box
                component="video"
                data-testid="wa-preview-media"
                controls
                src={media.url}
                aria-label={`Header video${
                    media.fileName ? ` ${media.fileName}` : ''
                }`}
                sx={mediaSx}
            />
        )
    return (
        <Box data-testid="wa-preview-media" sx={tileSx}>
            <InsertDriveFileIcon
                sx={{ fontSize: ICON.xl, color: WHATSAPP_PREVIEW.meta }}
            />
            <Typography
                sx={{
                    flex: 1,
                    minWidth: 0,
                    color: WHATSAPP_PREVIEW.text,
                    typography: 'caption',
                    overflowWrap: 'anywhere',
                }}
            >
                {media.fileName || `${label} header`}
            </Typography>
            {media.url && media.kind === 'DOCUMENT' && (
                <Link
                    href={media.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: WHATSAPP_PREVIEW.link, typography: 'caption' }}
                >
                    Open
                </Link>
            )}
        </Box>
    )
}

HeaderMedia.propTypes = {
    media: PropTypes.shape({
        kind: PropTypes.oneOf(['IMAGE', 'VIDEO', 'DOCUMENT']).isRequired,
        url: PropTypes.string,
        fileName: PropTypes.string,
    }).isRequired,
}

/**
 * A WhatsApp template message as it shows on a phone: header text or media,
 * body, footer, the time and read ticks, then each button with its icon (a
 * link, a phone, a reply).
 *
 * One component for both places that preview a WhatsApp template, the
 * Create Template form (as the user types) and the Template Library (a saved
 * template), so the two cannot drift apart. `buttons` is taken in any shape
 * a template stores them in (a list, an object keyed by kind, or nothing;
 * see previewButtons), and every part but the body is optional.
 */
const WhatsAppMessagePreview = ({
    headerText = '',
    media = null,
    body = '',
    footer = '',
    buttons,
    emptyBodyText = 'Your message will appear here…',
}) => {
    const header = String(headerText ?? '').trim()
    const footerText = String(footer ?? '').trim()
    return (
        <Box
            data-testid="whatsapp-preview"
            sx={{
                background: WHATSAPP_PREVIEW.canvas,
                borderRadius: `${RADIUS.md}px`,
                p: 2.5,
                minHeight: 200,
                backgroundImage: WHATSAPP_PREVIEW.dots,
                backgroundSize: '20px 20px',
            }}
        >
            <Box
                sx={{
                    background: WHATSAPP_PREVIEW.bubble,
                    borderRadius: `0 ${RADIUS.sm}px ${RADIUS.sm}px ${RADIUS.sm}px`,
                    p: 1.5,
                    maxWidth: '85%',
                    boxShadow: WHATSAPP_PREVIEW.bubbleShadow,
                    position: 'relative',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: -6,
                        width: 0,
                        height: 0,
                        borderTop: `8px solid ${WHATSAPP_PREVIEW.bubble}`,
                        borderLeft: '6px solid transparent',
                    },
                }}
            >
                {media && <HeaderMedia media={media} />}
                {!media && header && (
                    <Typography
                        data-testid="wa-preview-header"
                        sx={{
                            color: WHATSAPP_PREVIEW.text,
                            typography: 'body1',
                            fontWeight: 700,
                            mb: 0.75,
                        }}
                    >
                        {header}
                    </Typography>
                )}
                <Typography
                    sx={{
                        color: WHATSAPP_PREVIEW.text,
                        typography: 'body2',
                        whiteSpace: 'pre-wrap',
                        overflowWrap: 'anywhere',
                        lineHeight: 1.5,
                        minHeight: 40,
                    }}
                >
                    {body || (
                        <Box
                            component="span"
                            sx={{
                                color: WHATSAPP_PREVIEW.placeholder,
                                fontStyle: 'italic',
                            }}
                        >
                            {emptyBodyText}
                        </Box>
                    )}
                </Typography>
                {footerText && (
                    <Typography
                        data-testid="wa-preview-footer"
                        sx={{
                            color: WHATSAPP_PREVIEW.meta,
                            typography: 'caption',
                            mt: 0.75,
                        }}
                    >
                        {footerText}
                    </Typography>
                )}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        gap: 0.5,
                        mt: 0.75,
                    }}
                >
                    <Typography
                        sx={{
                            color: WHATSAPP_PREVIEW.meta,
                            typography: 'caption',
                        }}
                    >
                        now
                    </Typography>
                    <Typography
                        aria-hidden="true"
                        sx={{
                            color: WHATSAPP_PREVIEW.link,
                            typography: 'caption',
                            lineHeight: 1,
                        }}
                    >
                        ✓✓
                    </Typography>
                </Box>
            </Box>
            {previewButtons(buttons).map((button, index) => {
                const Icon = BUTTON_ICON[button.kind]
                return (
                    <Box
                        key={index}
                        data-testid="wa-preview-button"
                        data-kind={button.kind}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.75,
                            background: WHATSAPP_PREVIEW.bubble,
                            borderTop: `1px solid ${WHATSAPP_PREVIEW.divider}`,
                            borderRadius: `${RADIUS.sm}px`,
                            maxWidth: '85%',
                            mt: 0.5,
                            py: 1,
                            color: WHATSAPP_PREVIEW.link,
                        }}
                    >
                        {Icon && (
                            <Icon
                                aria-hidden="true"
                                sx={{
                                    fontSize: ICON.md,
                                    color: WHATSAPP_PREVIEW.link,
                                }}
                            />
                        )}
                        <Typography
                            sx={{
                                color: WHATSAPP_PREVIEW.link,
                                typography: 'body2',
                                fontWeight: 500,
                            }}
                        >
                            {button.text}
                        </Typography>
                    </Box>
                )
            })}
        </Box>
    )
}

WhatsAppMessagePreview.propTypes = {
    // Shown only when there is no media header.
    headerText: PropTypes.string,
    // A media header: its kind, a link to the file (an object URL for a
    // chosen file, the API's signed URL for a saved one) and its name.
    media: PropTypes.shape({
        kind: PropTypes.oneOf(['IMAGE', 'VIDEO', 'DOCUMENT']).isRequired,
        url: PropTypes.string,
        fileName: PropTypes.string,
    }),
    body: PropTypes.string,
    footer: PropTypes.string,
    // A list, an object keyed by button kind, or nothing (previewButtons).
    buttons: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    // What the bubble says while the body is empty.
    emptyBodyText: PropTypes.string,
}

export default WhatsAppMessagePreview
